import React, {
  ChangeEvent,
  useCallback,
  useState,
  useRef,
  useEffect,
} from 'react';
import {
  useDropzone,
  DropzoneRootProps,
  DropzoneInputProps,
} from 'react-dropzone';
import { Button, Progress } from 'antd';
import { useMutation, useApolloClient, useQuery } from '@apollo/client';
// API
import {
  CREATE_CAMPAIGN_DELIVERABLES_FILES,
  SUBMIT_DELIVERABLES_FOR_REVIEW,
  // Helpers
} from 'api/campaignV2/mutations';
import {
  uploadDeliverables,
  UploadFileType,
  isImageType,
} from 'helpers/campaign';
import {
  uploadDeliverableFileWithConversion,
  isMp4Video,
} from 'helpers/deliverableFileUpload';
import { isSupportedVideoFormat } from 'helpers/videoFormats';
import { GET_VIDEO_TRANSCODING_JOBS } from 'api/campaignV2/queries';
import {
  GetVideoTranscodingJobs,
  GetVideoTranscodingJobsVariables,
} from 'api/campaignV2/types/GetVideoTranscodingJobs';
import { MediaJobStatus } from 'api/graphql-global-types';
// Constants
import { MAX_FILES, MAX_IMAGE_SIZE, MAX_VIDEO_SIZE } from './constants';
// Types
import {
  CampaignOfferStatus,
  CampaignTaskType,
  CBDeliverableItemType,
  CampaignType,
} from 'api/graphql-global-types';
import {
  SubmitDeliverablesForReview,
  SubmitDeliverablesForReviewVariables,
} from 'api/campaignV2/types/SubmitDeliverablesForReview';
import {
  CreateCampaignDeliverableFiles,
  CreateCampaignDeliverableFilesVariables,
} from 'api/campaignV2/types/CreateCampaignDeliverableFiles';
import {
  GetCampaignOffers_getCampaignOffers_entities,
  GetCampaignOffers_getCampaignOffers_entities_campaign_activities_SocialActivity_tasks,
} from 'api/campaignV2/types/GetCampaignOffers';
// UiShared
import TextArea from 'uiShared/Textarea/Textarea';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import SubmitV2 from './components/SubmitV2/SubmitV2';
import SubmitTimelineDeliverables from './components/SubmitTimelineDeliverables/SubmitTimelineDeliverables';
// Styles
import styles from './CampaignDeliverablesSubmitForReviewForm.module.scss';

type CampaignDeliverablesSubmitForReviewFormProps = {
  offer: GetCampaignOffers_getCampaignOffers_entities | null;
  isV2Campaign: boolean;
  isV3Campaign?: boolean;
  onFinish: () => void;
  refetch?: () => void;
};

export type TaskData = {
  postCopy: string;
  files: UploadFileType[];
  checked: boolean;
};

const SubmitV1 = ({
  handlePostCopyChange,
  showClear,
  isLoading,
  handleReset,
  getRootProps,
  getInputProps,
  uploadFiles,
  isSocialShareCampaign = false,
}: {
  uploadFiles: UploadFileType[];
  showClear: boolean;
  isLoading: boolean;
  handlePostCopyChange: (newValue: ChangeEvent<HTMLTextAreaElement>) => void;
  handleReset: () => void;
  getRootProps: () => DropzoneRootProps;
  getInputProps: () => DropzoneInputProps;
  isSocialShareCampaign?: boolean;
}) => {
  return (
    <>
      {/* Only show post copy for non-social share campaigns */}
      {!isSocialShareCampaign && (
        <div className={styles.inputWrapper}>
          <p className={styles.label}>Post Copy</p>
          <TextArea
            placeholder="Please write the copy you'd like to use for your post(s). Make sure to review the campaign details carefully for any hashtags, accounts, links, or other required verbiage."
            name="postCopy"
            onChange={handlePostCopyChange}
            className={styles.textarea}
            rows={3}
          />
        </div>
      )}

      {showClear && (
        <Button loading={isLoading} onClick={handleReset}>
          Clear Files
        </Button>
      )}

      <div className={styles.dropzoneWrapper} {...getRootProps()}>
        <input {...getInputProps()} />
        <p className={styles.title}>Drag & drop or click to select files.</p>
        <p className={styles.subtitle}>
          Upload JPEG and PNG images or mp4/mov video files.
        </p>
        <div className={styles.previewsContainer}>
          {uploadFiles.map((file, index) => {
            const isImage = file.path?.match(/\.(jpeg|jpg|png)$/i);
            const isVideo = file.path?.match(/\.(mp4|mov)$/i);

            return (
              <div key={index} className={styles.previewWrapper}>
                <p className={styles.fileName}>{file.name}</p>
                {isImage ? (
                  <img
                    src={file.preview}
                    alt={file.name}
                    className={styles.preview}
                  />
                ) : isVideo ? (
                  <video
                    src={file.preview}
                    controls
                    className={styles.preview}
                    crossOrigin="anonymous"
                  >
                    <track
                      kind="captions"
                      label="English captions"
                      srcLang="en"
                      src=""
                      default
                    />
                    <source src={file.preview} type={file.type} />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <p>Unsupported file type</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

const CampaignDeliverablesSubmitForReviewForm = ({
  offer,
  isV2Campaign,
  isV3Campaign = false,
  onFinish,
  refetch,
}: CampaignDeliverablesSubmitForReviewFormProps): JSX.Element => {
  const [postCopyText, setPostCopyText] = useState<string>('');
  const [uploadFiles, setUploadFiles] = useState<UploadFileType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [taskData, setTaskData] = useState<Record<string, TaskData>>({});
  const [uploadProgress, setUploadProgress] = useState<{
    fileName: string;
    progress: number;
    stage: 'uploading' | 'converting';
  } | null>(null);
  const [transcodingState, setTranscodingState] = useState<{
    jobIds: string[];
    results: Map<string, string>;
    error: string | null;
  }>({
    jobIds: [],
    results: new Map(),
    error: null,
  });
  const isTranscodingCompleteRef = useRef<boolean>(false);
  const transcodingCompletePromiseRef = useRef<{
    resolve: (results: Map<string, string>) => void;
    reject: (error: Error) => void;
  } | null>(null);

  const [createCampaignDeliverableFiles] = useMutation<
    CreateCampaignDeliverableFiles,
    CreateCampaignDeliverableFilesVariables
  >(CREATE_CAMPAIGN_DELIVERABLES_FILES);

  const [submitDeliverablesForReview] = useMutation<
    SubmitDeliverablesForReview,
    SubmitDeliverablesForReviewVariables
  >(SUBMIT_DELIVERABLES_FOR_REVIEW);

  const apolloClient = useApolloClient();
  const offerId = offer?.id || '';
  const campaignId = offer?.campaign?.id || '';
  const tasks: GetCampaignOffers_getCampaignOffers_entities_campaign_activities_SocialActivity_tasks[] =
    (offer?.campaign?.activities?.[0] as any)?.tasks || [];

  const isSocialShareCampaign =
    offer?.campaign?.type === CampaignType.SocialShare;

  // Poll transcoding job status every 2s until complete
  const { data: transcodingData } = useQuery<
    GetVideoTranscodingJobs,
    GetVideoTranscodingJobsVariables
  >(GET_VIDEO_TRANSCODING_JOBS, {
    variables: {
      input: {
        ids: transcodingState.jobIds,
      },
    },
    skip:
      transcodingState.jobIds.length === 0 || isTranscodingCompleteRef.current,
    pollInterval: isTranscodingCompleteRef.current ? 0 : 2000,
    fetchPolicy: 'network-only',
    onError: (error) => {
      setTranscodingState((prev) => ({
        ...prev,
        error: error.message,
      }));
    },
  });

  useEffect(() => {
    if (
      !transcodingData?.getVideoTranscodingJobs ||
      transcodingState.jobIds.length === 0
    ) {
      return;
    }

    const jobs = transcodingData.getVideoTranscodingJobs;

    setTranscodingState((prev) => {
      const newResults = new Map(prev.results);
      let hasFailedJob = false;
      let completedCount = 0;

      // Process job results: track completed/failed, resolve Promise when all done
      for (const job of jobs) {
        if (job.status === MediaJobStatus.COMPLETED && job.resultKey) {
          newResults.set(job.id, job.resultKey);
          completedCount++;
        } else if (job.status === MediaJobStatus.FAILED) {
          hasFailedJob = true;
          const errorMsg = `Transcoding job ${job.id} failed: ${
            job.errorMessage || 'Unknown error'
          }`;
          const newState = { ...prev, results: newResults, error: errorMsg };
          if (transcodingCompletePromiseRef.current) {
            transcodingCompletePromiseRef.current.reject(new Error(errorMsg));
            transcodingCompletePromiseRef.current = null;
          }
          return newState;
        }
      }

      if (completedCount > 0 || hasFailedJob) {
        const progress = Math.round(
          (completedCount / prev.jobIds.length) * 100
        );
        setUploadProgress({
          fileName: 'Videos',
          progress,
          stage: 'converting',
        });
      }

      if (completedCount === prev.jobIds.length) {
        isTranscodingCompleteRef.current = true;
        setUploadProgress(null);
        if (transcodingCompletePromiseRef.current) {
          transcodingCompletePromiseRef.current.resolve(newResults);
          transcodingCompletePromiseRef.current = null;
        }
      } else if (hasFailedJob) {
        isTranscodingCompleteRef.current = true;
        setUploadProgress(null);
        if (transcodingCompletePromiseRef.current) {
          const errorMsg = prev.error || 'Transcoding failed';
          transcodingCompletePromiseRef.current.reject(new Error(errorMsg));
          transcodingCompletePromiseRef.current = null;
        }
      }

      return { ...prev, results: newResults };
    });
  }, [transcodingData, transcodingState.jobIds]);

  const handleTaskDataChange = (taskId: string, data: TaskData) => {
    setTaskData((prev) => ({
      ...prev,
      [taskId]: data,
    }));
  };

  // Extract job ID from various formats (string, object with id, etc.)
  const extractJobId = (convertJobId: string | undefined): string => {
    if (!convertJobId) {
      return '';
    }
    return typeof convertJobId === 'string'
      ? convertJobId
      : (convertJobId as any)?.id || String(convertJobId);
  };

  // Wait for transcoding jobs to complete using Promise-based approach
  // Resolved by useEffect when polling detects all jobs are done
  const waitForTranscoding = async (
    jobIds: string[]
  ): Promise<Map<string, string>> => {
    if (jobIds.length === 0) {
      return new Map();
    }

    isTranscodingCompleteRef.current = false;
    setTranscodingState({
      jobIds,
      results: new Map(),
      error: null,
    });
    setUploadProgress({
      fileName: 'Videos',
      progress: 0,
      stage: 'converting',
    });

    return new Promise<Map<string, string>>((resolve, reject) => {
      if (isTranscodingCompleteRef.current) {
        const error = transcodingState.error;
        if (error) {
          reject(new Error(error));
          return;
        }
        resolve(new Map(transcodingState.results));
        return;
      }

      transcodingCompletePromiseRef.current = { resolve, reject };

      if (transcodingState.error) {
        reject(new Error(transcodingState.error));
        transcodingCompletePromiseRef.current = null;
      }
    });
  };

  const validateV1Submission = (): boolean => {
    if (!uploadFiles || uploadFiles.length === 0) {
      errorNotification('Please upload at least one file');
      return false;
    }

    if (!postCopyText || postCopyText.trim() === '') {
      errorNotification('Please provide post copy');
      return false;
    }

    return true;
  };

  // Separate files: images/MP4 use old upload, non-MP4 videos use multipart with transcoding
  const separateFilesByType = (
    files: UploadFileType[]
  ): {
    imageFiles: File[];
    mp4VideoFiles: File[];
    nonMp4VideoFiles: File[];
  } => {
    const imageFiles: File[] = [];
    const mp4VideoFiles: File[] = [];
    const nonMp4VideoFiles: File[] = [];

    for (const file of files) {
      if (isImageType(file as File)) {
        imageFiles.push(file as File);
      } else if (isMp4Video(file as File)) {
        mp4VideoFiles.push(file as File);
      } else {
        nonMp4VideoFiles.push(file as File);
      }
    }

    return { imageFiles, mp4VideoFiles, nonMp4VideoFiles };
  };

  // Upload image using old presigned URL method
  const uploadImageFile = async (
    file: File
  ): Promise<{ key: string; name: string }> => {
    setUploadProgress({
      fileName: file.name,
      progress: 0,
      stage: 'uploading',
    });

    const { data: presignedUrls } = await createCampaignDeliverableFiles({
      variables: {
        input: {
          files: [{ name: file.name }],
        },
      },
    });

    if (!presignedUrls?.createCampaignDeliverableFiles.length) {
      throw new Error('Failed to get presigned URL for image');
    }

    const uploaded = await uploadDeliverables(
      presignedUrls.createCampaignDeliverableFiles,
      [file]
    );

    if (!uploaded.length || !uploaded[0].key) {
      throw new Error('Failed to upload image');
    }

    return {
      key: uploaded[0].key,
      name: file.name,
    };
  };

  // Upload MP4 video using old presigned URL method (no transcoding needed)
  const uploadMp4VideoFile = async (
    file: File
  ): Promise<{ key: string; name: string }> => {
    setUploadProgress({
      fileName: file.name,
      progress: 0,
      stage: 'uploading',
    });

    const { data: presignedUrls } = await createCampaignDeliverableFiles({
      variables: {
        input: {
          files: [{ name: file.name }],
        },
      },
    });

    if (!presignedUrls?.createCampaignDeliverableFiles.length) {
      throw new Error('Failed to get presigned URL for MP4 video');
    }

    const uploaded = await uploadDeliverables(
      presignedUrls.createCampaignDeliverableFiles,
      [file]
    );

    if (!uploaded.length || !uploaded[0].key) {
      throw new Error('Failed to upload MP4 video');
    }

    return {
      key: uploaded[0].key,
      name: file.name,
    };
  };

  // Upload non-MP4 video using multipart upload with transcoding
  const uploadNonMp4VideoFile = async (
    file: File
  ): Promise<{
    key: string;
    name: string;
    convertJobId?: string;
  }> => {
    setUploadProgress({
      fileName: file.name,
      progress: 0,
      stage: 'uploading',
    });

    const result = await uploadDeliverableFileWithConversion(
      file,
      campaignId,
      offerId,
      apolloClient,
      (percentage) => {
        setUploadProgress({
          fileName: file.name,
          progress: percentage,
          stage: 'uploading',
        });
      }
    );

    return {
      key: result.key,
      name: result.name,
      convertJobId: result.needsConversion ? result.convertJobId : undefined,
    };
  };

  // Update file keys with transcoded resultKey from completed jobs
  const updateFileKeysWithTranscodingResults = (
    uploadedUrls: Array<{
      key: string;
      name: string;
      convertJobId?: string;
    }>,
    resultsMap: Map<string, string>
  ): void => {
    for (let i = 0; i < uploadedUrls.length; i++) {
      const uploadedUrl = uploadedUrls[i];
      if (uploadedUrl.convertJobId) {
        const jobId = extractJobId(uploadedUrl.convertJobId);
        const resultKey = resultsMap.get(jobId);
        if (!resultKey) {
          const availableJobIds = Array.from(resultsMap.keys());
          throw new Error(
            `Transcoding job ${jobId} did not produce a resultKey for file ${
              uploadedUrl.name
            }. Available job IDs: ${availableJobIds.join(
              ', '
            )}. Submission cannot proceed without resultKey.`
          );
        }
        uploadedUrl.key = resultKey;
      }
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      isTranscodingCompleteRef.current = false;
      setTranscodingState({
        jobIds: [],
        results: new Map(),
        error: null,
      });

      if (isV3Campaign) {
        return; // V3 handled by SubmitTimelineDeliverables component
      }

      if (isV2Campaign) {
        if (!taskData || Object.keys(taskData).length === 0) {
          errorNotification('Invalid submission data');
          return;
        }

        try {
          const uploadedTaskData = { ...taskData };
          const allConversionJobIds: string[] = [];

          for (const task of tasks) {
            const taskDataItem = taskData[task.id];
            if (!taskDataItem) continue;

            const isContentTask = task.type === CampaignTaskType.CONTENT;

            if (
              isContentTask &&
              taskDataItem.files &&
              taskDataItem.files.length > 0
            ) {
              const uploadedFiles: Array<{
                key: string;
                name: string;
                convertJobId?: string;
              }> = [];

              const { imageFiles, mp4VideoFiles, nonMp4VideoFiles } =
                separateFilesByType(taskDataItem.files as File[]);

              for (const file of imageFiles) {
                try {
                  const result = await uploadImageFile(file);
                  uploadedFiles.push(result);
                } catch (error: any) {
                  setUploadProgress(null);
                  errorNotification(
                    `Failed to upload ${file.name}: ${error.message}`
                  );
                  return;
                }
              }

              for (const file of mp4VideoFiles) {
                try {
                  const result = await uploadMp4VideoFile(file);
                  uploadedFiles.push(result);
                } catch (error: any) {
                  setUploadProgress(null);
                  errorNotification(
                    `Failed to upload ${file.name}: ${error.message}`
                  );
                  return;
                }
              }

              const conversionJobIds: string[] = [];
              // Non-MP4 videos: multipart upload + transcoding
              for (const file of nonMp4VideoFiles) {
                try {
                  setUploadProgress({
                    fileName: file.name,
                    progress: 0,
                    stage: 'uploading',
                  });

                  const result = await uploadDeliverableFileWithConversion(
                    file,
                    campaignId,
                    task.id,
                    apolloClient,
                    (percentage) => {
                      setUploadProgress({
                        fileName: file.name,
                        progress: percentage,
                        stage: 'uploading',
                      });
                    }
                  );

                  uploadedFiles.push({
                    key: result.key,
                    name: result.name,
                    convertJobId: result.needsConversion
                      ? result.convertJobId
                      : undefined,
                  });

                  if (result.needsConversion && result.convertJobId) {
                    conversionJobIds.push(result.convertJobId);
                  }
                } catch (error: any) {
                  setUploadProgress(null);
                  errorNotification(
                    `Failed to upload ${file.name}: ${error.message}`
                  );
                  return;
                }
              }

              if (conversionJobIds.length > 0) {
                allConversionJobIds.push(...conversionJobIds);
              }

              uploadedTaskData[task.id] = {
                ...taskDataItem,
                files: uploadedFiles.map((uploadedFile) => {
                  const originalFile = taskDataItem.files.find(
                    (f) => (f as File).name === uploadedFile.name
                  ) as File;

                  return {
                    ...originalFile,
                    key: uploadedFile.key,
                    name: uploadedFile.name,
                    convertJobId: uploadedFile.convertJobId,
                  } as UploadFileType & { convertJobId?: string };
                }),
              };
            }
          }

          if (allConversionJobIds.length > 0) {
            setTranscodingState((prev) => ({
              ...prev,
              jobIds: allConversionJobIds,
            }));
            setUploadProgress({
              fileName: 'Videos',
              progress: 0,
              stage: 'converting',
            });

            const resultsMap = await waitForTranscoding(allConversionJobIds);

            for (const task of tasks) {
              const taskDataItem = uploadedTaskData[task.id];
              if (!taskDataItem || task.type !== CampaignTaskType.CONTENT) {
                continue;
              }

              uploadedTaskData[task.id] = {
                ...taskDataItem,
                files: taskDataItem.files.map((file: any) => {
                  if (file.convertJobId) {
                    const jobId = extractJobId(file.convertJobId);
                    const resultKey = resultsMap.get(jobId);
                    if (!resultKey) {
                      const availableJobIds = Array.from(resultsMap.keys());
                      throw new Error(
                        `Transcoding job ${jobId} did not produce a resultKey for file ${
                          file.name
                        }. Available job IDs: ${availableJobIds.join(
                          ', '
                        )}. Submission cannot proceed without resultKey.`
                      );
                    }

                    return {
                      ...file,
                      key: resultKey,
                    };
                  }

                  return file;
                }),
              };
            }
          }

          const items = tasks.flatMap((task: any) => {
            const taskDataItem = uploadedTaskData[task.id];

            if (!taskDataItem) {
              errorNotification('Please complete deliverables form');
              return [];
            }

            const isContentTask = task.type === CampaignTaskType.CONTENT;

            if (
              isContentTask &&
              (!taskDataItem.files || !taskDataItem.files.length)
            ) {
              errorNotification(`Missing files for task: ${task.title}`);
              return [];
            }

            if (
              !isContentTask &&
              (!taskDataItem.postCopy || taskDataItem.postCopy.trim() === '')
            ) {
              errorNotification(`Missing post copy for task: ${task.title}`);
              return [];
            }

            if (isContentTask) {
              return taskDataItem.files.map((file: any) => {
                const submitItem: any = {
                  taskId: task.id,
                  copy: taskDataItem.postCopy || '',
                  name: file.name,
                  type: CBDeliverableItemType.FILE,
                  key: file.key,
                };

                if (file.convertJobId) {
                  if (!file.key) {
                    throw new Error(
                      `File ${file.name} is missing key. This should be resultKey from transcoding.`
                    );
                  }
                  submitItem.videoTranscodingJobId = file.convertJobId;
                }

                return submitItem;
              });
            } else {
              return [
                {
                  taskId: task.id,
                  copy: taskDataItem.postCopy || '',
                  type: CBDeliverableItemType.COPY,
                },
              ];
            }
          });

          const result = await submitDeliverablesForReview({
            variables: {
              input: {
                offerId,
                items,
              },
            },
          });
          const data = result.data;

          const status = data?.submitDeliverablesForReview?.statusV2;

          if (status === CampaignOfferStatus.DELIVERABLES_APPROVED) {
            successNotification('Deliverables have been approved!');
          } else {
            successNotification('Deliverables submitted for review.');
          }
        } catch (error: any) {
          errorNotification(
            error?.message || 'Error submitting deliverables for review'
          );
        }
      } else {
        if (!validateV1Submission()) {
          setIsLoading(false);
          return;
        }

        // V1: Upload images, MP4 videos (old method), and non-MP4 videos (multipart + transcoding)
        const { imageFiles, mp4VideoFiles, nonMp4VideoFiles } =
          separateFilesByType(uploadFiles);

        const uploadedUrls: Array<{
          key: string;
          name: string;
          convertJobId?: string;
        }> = [];
        const allConversionJobIds: string[] = [];

        for (const file of imageFiles) {
          try {
            const result = await uploadImageFile(file);
            uploadedUrls.push(result);
          } catch (error: any) {
            setUploadProgress(null);
            errorNotification(
              `Failed to upload ${file.name}: ${error.message}`
            );
            setIsLoading(false);
            return;
          }
        }

        for (const file of mp4VideoFiles) {
          try {
            const result = await uploadMp4VideoFile(file);
            uploadedUrls.push(result);
          } catch (error: any) {
            setUploadProgress(null);
            errorNotification(
              `Failed to upload ${file.name}: ${error.message}`
            );
            setIsLoading(false);
            return;
          }
        }

        // Non-MP4 videos: multipart upload + transcoding
        for (const file of nonMp4VideoFiles) {
          try {
            const result = await uploadNonMp4VideoFile(file);
            uploadedUrls.push(result);

            if (result.convertJobId) {
              allConversionJobIds.push(result.convertJobId);
            }
          } catch (error: any) {
            setUploadProgress(null);
            errorNotification(
              `Failed to upload ${file.name}: ${error.message}`
            );
            setIsLoading(false);
            return;
          }
        }

        try {
          const resultsMap = await waitForTranscoding(allConversionJobIds);
          updateFileKeysWithTranscodingResults(uploadedUrls, resultsMap);
        } catch (error: any) {
          setUploadProgress(null);
          errorNotification(
            `Transcoding failed: ${error.message || 'Unknown error'}`
          );
          setIsLoading(false);
          return;
        }

        try {
          const { data } = await submitDeliverablesForReview({
            variables: {
              input: {
                offerId,
                postCopy: postCopyText,
                files: uploadedUrls.map((item) => ({
                  key: item.key,
                  name: item.name,
                })),
              },
            },
          });

          if (
            data?.submitDeliverablesForReview.statusV2 ===
            CampaignOfferStatus.DELIVERABLES_APPROVED
          ) {
            successNotification('Deliverables have been approved!');
          } else {
            successNotification('Items sent for review successfully.');
          }
        } catch (error: any) {
          errorNotification(
            `Failed to submit deliverables: ${error.message || 'Unknown error'}`
          );
          setIsLoading(false);
          return;
        }
      }

      setIsLoading(false);
      setUploadProgress(null);

      if (refetch) {
        await refetch();
      }

      handleFinish();
    } catch (error: any) {
      errorNotification(
        error?.message || 'Something went wrong. Please try again.'
      );
      setIsLoading(false);
      setUploadProgress(null);
    }
  };

  const handlePostCopyChange = (newValue: ChangeEvent<HTMLTextAreaElement>) => {
    const newPost = newValue.target.value;
    if (newPost !== '') {
      setPostCopyText(newPost);
    } else {
      setPostCopyText('');
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const validFiles = acceptedFiles.filter((file) => {
        const isVideo = isSupportedVideoFormat(file.type);
        const isImage = file.type === 'image/jpeg' || file.type === 'image/png';
        const isValidSize = isVideo
          ? file.size <= MAX_VIDEO_SIZE
          : file.size <= MAX_IMAGE_SIZE;
        return (isVideo || isImage) && isValidSize;
      });

      const filesWithPreview: UploadFileType[] = validFiles.map((file) => {
        return Object.assign(file, {
          preview: URL.createObjectURL(file),
          fileName: file.name,
        });
      });

      const totalFiles = uploadFiles.length + filesWithPreview.length;

      if (totalFiles > MAX_FILES) {
        errorNotification(
          `Please upload up to ${MAX_FILES} files. You are trying to upload ${totalFiles}!`
        );
      } else setUploadFiles((prevFiles) => [...prevFiles, ...filesWithPreview]);
    },
    [uploadFiles.length]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: MAX_FILES,
    // Disable accept validation in dropzone - we handle it manually in onDrop
    // This avoids the attr-accept bug with object format
    noClick: false,
    noKeyboard: false,
  });

  // Manual file validation in onDrop to avoid attr-accept bug with object format
  const handleReset = () => {
    setUploadFiles([]);
  };

  const handleFinish = () => {
    handleReset();
    onFinish();
  };

  const showClear = Boolean(uploadFiles.length);
  const isUploadDisabled = isV3Campaign
    ? false // V3 handles its own validation
    : isV2Campaign
    ? Object.keys(taskData).length === 0 ||
      tasks.some((task: any) => {
        // Don't validate IG_STORY tasks since they don't need copy
        if (task.type === CampaignTaskType.IG_STORY) {
          return false;
        }

        const taskDataItem = taskData[task.id];
        if (!taskDataItem) return true;

        if (!taskDataItem.checked) return true;

        const isContentTask = task.type === CampaignTaskType.CONTENT;

        if (isContentTask) {
          return !taskDataItem.files.length;
        } else {
          return !taskDataItem.postCopy.trim();
        }
      })
    : isSocialShareCampaign
    ? !uploadFiles.length // For social share campaigns, only require files
    : !uploadFiles.length || !postCopyText; // For other V1 campaigns, require both files and post copy

  const uploadProgressText =
    uploadProgress?.stage === 'uploading'
      ? `Uploading ${uploadProgress?.fileName}...`
      : `Converting ${uploadProgress?.fileName}...`;

  return (
    <div className={styles.root}>
      {isV3Campaign ? (
        <SubmitTimelineDeliverables offer={offer} refetch={refetch} />
      ) : isV2Campaign ? (
        <SubmitV2
          tasks={tasks}
          taskData={taskData}
          onTaskDataChange={handleTaskDataChange}
          isLoading={isLoading}
        />
      ) : (
        <SubmitV1
          handlePostCopyChange={handlePostCopyChange}
          showClear={showClear}
          isLoading={isLoading}
          handleReset={handleReset}
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          uploadFiles={uploadFiles}
          isSocialShareCampaign={isSocialShareCampaign}
        />
      )}
      {uploadProgress && (
        <div className={styles.progressContainer}>
          <Progress
            percent={uploadProgress.progress}
            status="active"
            format={(percent) => `${percent}%`}
          />
          <p className={styles.progressText}>{uploadProgressText}</p>
        </div>
      )}

      {!isV3Campaign && (
        <Button
          disabled={isUploadDisabled}
          loading={isLoading}
          onClick={handleSubmit}
        >
          Upload Files
        </Button>
      )}
    </div>
  );
};

export default CampaignDeliverablesSubmitForReviewForm;
